/**
 * 应用状态管理
 * 处理主题、布局、设置等全局状态
 */

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'

// 主题模式
export type ThemeMode = 'light' | 'dark' | 'auto'

// 布局模式
export type LayoutMode = 'vertical' | 'horizontal' | 'mix'

// 应用设置接口
export interface AppSettings {
  // 主题相关
  themeMode: ThemeMode
  primaryColor: string

  // 布局相关
  layoutMode: LayoutMode
  sidebarCollapsed: boolean
  fixedHeader: boolean
  fixedSidebar: boolean

  // 功能开关
  showTabs: boolean
  showBreadcrumb: boolean
  showFooter: boolean
  showLogo: boolean

  // 动画效果
  enableTransition: boolean
  transitionName: string

  // 其他设置
  language: string
  size: 'large' | 'default' | 'small'
  watermark: boolean
  grayMode: boolean
  colorWeakness: boolean
}

// 默认设置
const defaultSettings: AppSettings = {
  // 主题相关
  themeMode: 'light',
  primaryColor: '#3b82f6',

  // 布局相关
  layoutMode: 'vertical',
  sidebarCollapsed: false,
  fixedHeader: true,
  fixedSidebar: true,

  // 功能开关
  showTabs: true,
  showBreadcrumb: true,
  showFooter: false,
  showLogo: true,

  // 动画效果
  enableTransition: true,
  transitionName: 'fade-transform',

  // 其他设置
  language: 'zh-CN',
  size: 'default',
  watermark: false,
  grayMode: false,
  colorWeakness: false
}

export const useAppStore = defineStore('app', () => {
  // 状态
  const settings = ref<AppSettings>({ ...defaultSettings })
  const loading = ref(false)
  const cachedViews = ref<string[]>([])
  const visitedViews = ref<any[]>([])
  const isMobile = ref(false)

  // 计算属性
  const isDark = computed(() => {
    if (settings.value.themeMode === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return settings.value.themeMode === 'dark'
  })

  const isLight = computed(() => !isDark.value)

  const currentTheme = computed(() => isDark.value ? 'dark' : 'light')

  // 设备检测计算属性
  const isTablet = computed(() => {
    const width = window.innerWidth
    return width >= 768 && width < 1024
  })

  const isDesktop = computed(() => {
    return window.innerWidth >= 1024
  })

  // 主题相关方法
  const setThemeMode = (mode: ThemeMode) => {
    settings.value.themeMode = mode
    applyTheme()
    saveSettings()
  }

  const toggleTheme = () => {
    const newMode = isDark.value ? 'light' : 'dark'
    setThemeMode(newMode)
  }

  const setPrimaryColor = (color: string) => {
    settings.value.primaryColor = color
    applyPrimaryColor(color)
    saveSettings()
  }

  const applyTheme = () => {
    const html = document.documentElement

    // 移除所有主题类
    html.classList.remove('light', 'dark')

    // 添加当前主题类
    html.classList.add(currentTheme.value)

    // 设置CSS变量
    if (isDark.value) {
      html.classList.add('dark')
    } else {
      html.classList.remove('dark')
    }
  }

  const applyPrimaryColor = (color: string) => {
    const html = document.documentElement

    // 设置主题色CSS变量
    html.style.setProperty('--el-color-primary', color)

    // 生成主题色的各种变体
    const colors = generateThemeColors(color)
    Object.entries(colors).forEach(([key, value]) => {
      html.style.setProperty(key, value)
    })
  }

  const generateThemeColors = (primaryColor: string) => {
    // 这里应该实现颜色变体生成逻辑
    // 简化版本，实际项目中可以使用更复杂的颜色计算
    return {
      '--el-color-primary-light-3': lighten(primaryColor, 0.3),
      '--el-color-primary-light-5': lighten(primaryColor, 0.5),
      '--el-color-primary-light-7': lighten(primaryColor, 0.7),
      '--el-color-primary-light-8': lighten(primaryColor, 0.8),
      '--el-color-primary-light-9': lighten(primaryColor, 0.9),
      '--el-color-primary-dark-2': darken(primaryColor, 0.2)
    }
  }

  /**
   * 颜色变亮函数
   * @param color 十六进制颜色值
   * @param amount 变亮程度 (0-1)
   * @returns 变亮后的颜色
   */
  const lighten = (color: string, amount: number): string => {
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)

    const newR = Math.min(255, Math.round(r + (255 - r) * amount))
    const newG = Math.min(255, Math.round(g + (255 - g) * amount))
    const newB = Math.min(255, Math.round(b + (255 - b) * amount))

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
  }

  /**
   * 颜色变暗函数
   * @param color 十六进制颜色值
   * @param amount 变暗程度 (0-1)
   * @returns 变暗后的颜色
   */
  const darken = (color: string, amount: number): string => {
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)

    const newR = Math.max(0, Math.round(r * (1 - amount)))
    const newG = Math.max(0, Math.round(g * (1 - amount)))
    const newB = Math.max(0, Math.round(b * (1 - amount)))

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
  }

  // 布局相关方法
  const setLayoutMode = (mode: LayoutMode) => {
    settings.value.layoutMode = mode
    saveSettings()
  }

  const toggleSidebar = () => {
    settings.value.sidebarCollapsed = !settings.value.sidebarCollapsed
    saveSettings()
  }

  const setSidebarCollapsed = (collapsed: boolean) => {
    settings.value.sidebarCollapsed = collapsed

    // 记录用户是否手动设置了收起状态
    if (!isMobile.value) {
      if (collapsed) {
        localStorage.setItem('user-manually-collapsed', 'true')
      } else {
        localStorage.removeItem('user-manually-collapsed')
      }
    }

    saveSettings()
  }

  const setFixedHeader = (fixed: boolean) => {
    settings.value.fixedHeader = fixed
    saveSettings()
  }

  const setFixedSidebar = (fixed: boolean) => {
    settings.value.fixedSidebar = fixed
    saveSettings()
  }

  // 功能开关方法
  const setShowTabs = (show: boolean) => {
    settings.value.showTabs = show
    saveSettings()
  }

  const setShowBreadcrumb = (show: boolean) => {
    settings.value.showBreadcrumb = show
    saveSettings()
  }

  const setShowFooter = (show: boolean) => {
    settings.value.showFooter = show
    saveSettings()
  }

  const setShowLogo = (show: boolean) => {
    settings.value.showLogo = show
    saveSettings()
  }

  // 其他设置方法
  const setLanguage = (language: string) => {
    settings.value.language = language
    saveSettings()
  }

  const setSize = (size: 'large' | 'default' | 'small') => {
    settings.value.size = size
    saveSettings()
  }

  const setWatermark = (enabled: boolean) => {
    settings.value.watermark = enabled
    saveSettings()
  }

  const setGrayMode = (enabled: boolean) => {
    settings.value.grayMode = enabled
    const html = document.documentElement
    if (enabled) {
      html.classList.add('gray-mode')
    } else {
      html.classList.remove('gray-mode')
    }
    saveSettings()
  }

  const setColorWeakness = (enabled: boolean) => {
    settings.value.colorWeakness = enabled
    const html = document.documentElement
    if (enabled) {
      html.classList.add('color-weakness')
    } else {
      html.classList.remove('color-weakness')
    }
    saveSettings()
  }

  // 标签页管理
  const addCachedView = (name: string) => {
    if (!cachedViews.value.includes(name)) {
      cachedViews.value.push(name)
    }
  }

  const removeCachedView = (name: string) => {
    const index = cachedViews.value.indexOf(name)
    if (index > -1) {
      cachedViews.value.splice(index, 1)
    }
  }

  const clearCachedViews = () => {
    cachedViews.value = []
  }

  const addVisitedView = (view: any) => {
    const existingIndex = visitedViews.value.findIndex(v => v.path === view.path)
    if (existingIndex > -1) {
      visitedViews.value[existingIndex] = view
    } else {
      visitedViews.value.push(view)
    }
  }

  const removeVisitedView = (view: any) => {
    const index = visitedViews.value.findIndex(v => v.path === view.path)
    if (index > -1) {
      visitedViews.value.splice(index, 1)
    }
  }

  const clearVisitedViews = () => {
    visitedViews.value = []
  }

  // 设备检测
  const setMobile = (mobile: boolean) => {
    isMobile.value = mobile

    // 移动端自动收起侧边栏，但不影响桌面端的手动设置
    if (mobile && !settings.value.sidebarCollapsed) {
      setSidebarCollapsed(true)
    }
    // 桌面端恢复默认展开状态（如果之前是因为移动端而收起的）
    else if (!mobile && settings.value.sidebarCollapsed) {
      // 检查是否是用户手动设置的收起状态
      const userManuallyCollapsed = localStorage.getItem('user-manually-collapsed')
      if (!userManuallyCollapsed) {
        setSidebarCollapsed(false)
      }
    }
  }

  // 全局加载状态
  const setLoading = (loading_: boolean) => {
    loading.value = loading_
  }

  // 设置管理
  const resetSettings = () => {
    settings.value = { ...defaultSettings }
    applyTheme()
    applyPrimaryColor(settings.value.primaryColor)
    saveSettings()
    ElMessage.success('设置已重置')
  }

  /**
   * 更新设置
   * @param newSettings 新的设置项
   */
  const updateSettings = (newSettings: Partial<AppSettings>) => {
    settings.value = { ...settings.value, ...newSettings }

    // 如果更新了主题相关设置，重新应用主题
    if (newSettings.themeMode !== undefined) {
      applyTheme()
    }

    // 如果更新了主题色，重新应用主题色
    if (newSettings.primaryColor !== undefined) {
      applyPrimaryColor(newSettings.primaryColor)
    }

    // 如果更新了灰度模式，应用样式
    if (newSettings.grayMode !== undefined) {
      setGrayMode(newSettings.grayMode)
    }

    // 如果更新了色弱模式，应用样式
    if (newSettings.colorWeakness !== undefined) {
      setColorWeakness(newSettings.colorWeakness)
    }

    saveSettings()
  }

  // 保存设置到本地存储
  const saveSettings = () => {
    try {
      localStorage.setItem('app-settings', JSON.stringify(settings.value))
    } catch (error) {
      console.error('保存设置失败:', error)
    }
  }

  // 从本地存储加载设置
  const loadSettings = () => {
    try {
      const saved = localStorage.getItem('app-settings')
      if (saved) {
        const parsedSettings = JSON.parse(saved)
        settings.value = { ...defaultSettings, ...parsedSettings }
      }
    } catch (error) {
      console.error('加载设置失败:', error)
      settings.value = { ...defaultSettings }
    }
  }

  // 初始化应用
  const initApp = () => {
    loadSettings()
    applyTheme()
    applyPrimaryColor(settings.value.primaryColor)

    // 监听系统主题变化
    if (settings.value.themeMode === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      mediaQuery.addEventListener('change', applyTheme)
    }

    // 监听窗口大小变化
    const handleResize = () => {
      setMobile(window.innerWidth < 768)
    }
    window.addEventListener('resize', handleResize)
    handleResize()
  }

  // 监听设置变化
  watch(
    () => settings.value.themeMode,
    () => {
      applyTheme()
    }
  )

  watch(
    () => settings.value.primaryColor,
    (newColor) => {
      applyPrimaryColor(newColor)
    }
  )

  return {
    // 状态
    settings,
    loading,
    cachedViews,
    visitedViews,
    isMobile,

    // 计算属性
    isDark,
    isLight,
    currentTheme,
    isTablet,
    isDesktop,

    // 主题方法
    setThemeMode,
    toggleTheme,
    setPrimaryColor,

    // 布局方法
    setLayoutMode,
    toggleSidebar,
    setSidebarCollapsed,
    setFixedHeader,
    setFixedSidebar,

    // 功能开关方法
    setShowTabs,
    setShowBreadcrumb,
    setShowFooter,
    setShowLogo,

    // 其他设置方法
    setLanguage,
    setSize,
    setWatermark,
    setGrayMode,
    setColorWeakness,

    // 标签页管理
    addCachedView,
    removeCachedView,
    clearCachedViews,
    addVisitedView,
    removeVisitedView,
    clearVisitedViews,

    // 设备检测
    setMobile,

    // 全局状态
    setLoading,

    // 设置管理
    resetSettings,
    updateSettings,
    saveSettings,
    loadSettings,
    initApp
  }
})
